import { FeatureFlagOrganizationDTO } from './feature-flag'
import { TimeType } from './generic'
import { StructuredDataDisplayDTO, StructuredDataSchemaDTO, StructuredDataStatus } from './structured-data'

export enum Language {
  EN = 'en',
  FR = 'fr',
  ES = 'es',
  PT = 'pt',
}

export const LanguageFullName: { [key in Language]: string } = {
  [Language.EN]: 'English',
  [Language.FR]: 'French',
  [Language.ES]: 'Spanish',
  [Language.PT]: 'Portuguese',
}

export enum DemoType {
  POVERTY = 'POVERTY',
  JUSTICE = 'JUSTICE',
  EMPLOYMENT = 'EMPLOYMENT',
  HEALTH = 'HEALTH',
}

export enum LifeCycleStage {
  ONBOARDING = 'ONBOARDING',
  DEMO = 'DEMO',
  CLIENT = 'CLIENT',
  HEALTH = 'HEALTH',
  TRASH = 'TRASH',
}

export type HelperDefinition = {
  name: string
  nbArgs: number
  restrictions?: { argIndex: number; values: string[] }[]
}

export enum DataTemplateParam {
  BENEFICIARY = 'BENEFICIARY',
  RESOURCES = 'RESOURCES',
  CHAT_HISTORY = 'CHAT_HISTORY',
  ORGANIZATION = 'ORGANIZATION',
  SUGGESTED_CASEWORKER_ACTIONS = 'SUGGESTED_CASEWORKER_ACTIONS',
  SUGGESTED_BENEFICIARY_TASKS = 'SUGGESTED_BENEFICIARY_TASKS',
  STRUCTURED_DATA = 'STRUCTURED_DATA',
  NOW = 'NOW',
}

export const EqCustomHelper: HelperDefinition = { name: 'eq', nbArgs: 2 }
export const DataTemplateCustomHelper: HelperDefinition = {
  name: 'dataTemplate',
  nbArgs: 1,
  restrictions: [{ argIndex: 0, values: Object.values(DataTemplateParam) }],
}
export const PromptAllowedHelpers: HelperDefinition[] = [
  { name: 'if', nbArgs: 1 },
  { name: 'unless', nbArgs: 1 },
  { name: 'each', nbArgs: 1 },
  EqCustomHelper,
  DataTemplateCustomHelper,
]

export const PromptDefaultOwnerOrganisationId = `00000000-0000-0000-0000-000000000000`

export enum PromptTemplateType {
  BENEFICIARY_PROFILE = 'beneficiaryProfile',
  BENEFICIARY_PROFILE_FILE_LOAD = 'beneficiaryProfileFileLoad',
  BENEFICIARY_STRUCTURED_DATA = 'beneficiaryStructuredData',
  BENEFICIARY_TIPS = 'beneficiaryTips',
  CASEWORKER_DASHBOARD_SUGGEST = 'caseworkerDashboardSuggest',
  CHAT_CONVERSE = 'chatConverse',
  CHAT_WELCOME = 'chatWelcome',
  FILE_IMPORT = 'fileImport',
  FILE_SUMMARY_SUGGEST = 'fileSummarySuggest',
  ORGANIZATION_DESCRIPTION = 'organizationDescription',
  ORGANIZATION_METHODOLOGY = 'organizationMethodology',
  PROFILE_SUMMARY_SUGGEST = 'profileSummarySuggest',
  RESOURCES_FILE_LOAD = 'resourcesFileLoad',
  TASKS_SUGGEST = 'tasksSuggest',
}

export enum PromptType {
  ANSWER_ABOUT_BENEFICIARY = 'answerAboutBeneficiary',
  ANSWER_ABOUT_BENEFICIARIES = 'answerAboutBeneficiaries',
  CASEWORKER_TASKS = 'caseworkerTasks',
  FIND_STRUCTURED_DATA_ITEMS = 'findStructuredDataItems',
  _SAMPLE_PROMPT_SYNC_OBJECT = '_samplePromptSyncObject',
  _SAMPLE_PROMPT_SYNC_OBJECT_FILE = '_samplePromptSyncObjectFile',
  _SAMPLE_PROMPT_ASYNC_ARRAY = '_samplePromptAsyncArray',
}

export type PromptTemplateTypesForFile =
  | PromptTemplateType.FILE_SUMMARY_SUGGEST
  | PromptTemplateType.RESOURCES_FILE_LOAD
  | PromptTemplateType.BENEFICIARY_PROFILE_FILE_LOAD
  | PromptTemplateType.FILE_IMPORT

type OutputTypeWithTraceUrl = {
  traceUrl: string
}
export type OutputTypeForFILE_SUMMARY_SUGGEST = OutputTypeWithTraceUrl & {
  summary: string
}
export type OutputTypeForRESOURCES_FILE_LOAD = OutputTypeWithTraceUrl & {
  title: string
  summary: string
}
export type OutputTypeForBENEFICIARY_PROFILE_FILE_LOAD = OutputTypeWithTraceUrl & {
  fullName: string
  profile: string
}
export type OutputTypeForFILE_IMPORT = OutputTypeWithTraceUrl & {
  summary: string
}

export enum OnboardingStep {
  ORGANIZATION = 'organization',
  METHODOLOGY = 'methodology',
  SOURCES_PRIVATE = 'privateSources',
  MEMBERS = 'members',
  COMPLETED = 'completed',
}

export type CategorySettings = {
  name: string
}

export type OrganizationDTO = {
  lifeCycleStage: LifeCycleStage
  categoriesSettings?: CategorySettings[]
  chatAnswerGoal: string
  chatAssistantName: string
  chatMessageInstructions: string
  chatSafetyRules: string
  chatSettings?: {
    colors: {
      '--color-gradient-start': [number, number, number]
      '--color-gradient-end': [number, number, number]
      '--color-highlight': string
      '--color-primary-default': string
      '--color-primary-50': string
      '--color-primary-100': string
      '--color-primary-200': string
      '--color-primary-300': string
      '--color-primary-400': string
      '--color-primary-500': string
      '--color-primary-600': string
      '--color-primary-700': string
      '--color-primary-800': string
      '--color-primary-900': string
      '--color-primary-950': string
    }
    imageUrl: string
    allowFeedback?: boolean
  }
  commonSources: Record<string, { config: { subSources: string[] } }>
  description: string
  featureFlags: FeatureFlagOrganizationDTO[]
  id: string
  language: Language
  methodology: string
  name: string
  onboarding: {
    step: OnboardingStep
  }
  structuredDataDisplay: StructuredDataDisplayDTO
  structuredDataSchema: StructuredDataSchemaDTO
  structuredDataState?: {
    currentStatusEpoch: number
    currentStatus: StructuredDataStatus
    totalBeneficiaries: number
    totalUpdatedBeneficiaries: number
  }
  texts: {
    beneficiary_app: {
      information_notice: {
        en: string
        fr: string
        es: string
        pt: string
      }
      welcome: {
        en: string
        fr: string
        es: string
        pt: string
      }
    }
  } | null
  url?: string
  beneficiaryProfileTemplate: string | null
}

export type SudoContextOrganizationDTO = {
  lifeCycleStage: LifeCycleStage
  id: string
  name: string
  url?: string
  featureFlags: FeatureFlagOrganizationDTO[]
}

export type PromptTemplateDTO = {
  id: string
  promptType: PromptTemplateType
  revision: number
  revisionDate: TimeType
  promptText: string
}

export type PromptKeywordDTO = {
  key: string
  section: string
  apply: string
  isIterable: boolean
}

export type PromptErrorDTO = {
  name: string
  message: string
  text: string
  line: number
  col: number
}
